import { memo, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { Slider, SliderPropsColorOverrides } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";

import {
  CurrencyFieldComponent,
  MajorButtonComponent,
  SelectComponent,
  SpinnerComponent,
  TermsAndConditionsComponent,
} from "@zenith/components";
import {
  AMOUNT_INPUT_FIRST_REGEX,
  AMOUNT_INPUT_SECOND_REGEX,
  LOAN_INCREMENTAL_VALUE,
  SUCCESS_API_CODE,
  TERMS_AND_CONDITIONS,
} from "@zenith/data/constants";
import { formatCurrency } from "@zenith/utils";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import Clock from "@zenith/assets/icons/Clock";
import {
  useLoansContext,
  useServiceFailureContext,
} from "@zenith/context/global-context/hooks";
import { DASHBOARD, LOANS_SUCCESS } from "@zenith/routes/paths";
import {
  LoanApplicationDTO,
  LoanApplicationRecord,
  LoanDurationOptionDTO,
  LoanOfferDTO,
  LoanSimulationDTO,
  LoansApiService,
} from "@zenith/services/transactions";
import { createValidator } from "@zenith/utils/Helpers";

/* eslint-disable-next-line */
export interface LoansPageProps {}

interface LoansFormDataType {
  requestAmount: number;
  durationId: string;
  simulationId: string;
}

const LoansPage = memo(function LoansPage(props: LoansPageProps) {
  const { setLoansContext } = useLoansContext();
  const navigate = useNavigate();

  const [isEligible, setIsEligible] = useState<boolean>(false);
  const [loanIsValid, setLoanIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loanInfo, setLoanInfo] = useState<LoanOfferDTO>();
  const [simulationInfo, setSimulationInfo] = useState<LoanSimulationDTO>();
  const [options, setOptions] = useState<SelectItemsType[]>([]);
  const [formData, setFormData] = useState<LoansFormDataType>({
    requestAmount: 0,
    durationId: "",
    simulationId: "",
  });
  const validateField = createValidator(useServiceFailureContext());

  function getDuration(duration: LoanDurationOptionDTO[] | undefined) {
    if (duration) {
      return duration.map((durationOption) => {
        return {
          value: durationOption.id?.toString(),
          label: `${
            durationOption.unit
          } ${durationOption.chronoUnit?.toLowerCase()}`,
        } as SelectItemsType;
      });
    }
    return [];
  }

  useEffect(() => {
    LoansApiService.getLoanOffer()
      .then((response) => {
        if (response && "data" in response) {
          const durationOptions: SelectItemsType[] = getDuration(
            response.data?.durationOptions
          );
          setOptions(durationOptions);

          const eligible = response.internalCode === SUCCESS_API_CODE;
          setIsEligible(eligible);
          setLoanIsValid(eligible);
          setLoanInfo(response.data);
          setFormData({
            ...formData,
            requestAmount: validateField<number>(
              response.data?.loanStarting,
              "loadingStarting"
            ),
            durationId: validateField<string>(
              durationOptions.at(0)?.value,
              "duration"
            ),
          });
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }, []);

  useEffect(() => {
    if (isEligible && loanIsValid) {
      requestLoanSimulation();
    }
  }, [formData.durationId, loanIsValid]);

  function requestLoanSimulation() {
    const requestBody = {
      durationId: Number(formData.durationId),
      requestAmount: formData.requestAmount,
    };

    setIsLoading(true);
    LoansApiService.postLoanSimulation(requestBody)
      .then((response) => {
        if (response && "data" in response) {
          setSimulationInfo(response.data);
          if (response.data?.simulationId) {
            setFormData({
              ...formData,
              simulationId: validateField<string>(
                response.data.simulationId,
                "simulationId"
              ),
            });
          }
        }
      })
      .catch((error) => {
        throw new Error(error);
      })
      .finally(() => setIsLoading(false));
  }

  function handleLoanAmountInputChange(
    event: React.FocusEvent<HTMLInputElement>
  ): void {
    let value = event.target.value;
    value = value
      .replace(AMOUNT_INPUT_FIRST_REGEX, "")
      .replace(AMOUNT_INPUT_SECOND_REGEX, "$1");

    if (value.startsWith(".")) {
      value = `0${value}`;
    }

    const numericValue = parseFloat(value.replace(/,/g, ""));

    setFormData({ ...formData, requestAmount: numericValue });
  }

  function handleLoanAmountInputBlur(value: string): void {
    const numericValue = parseFloat(value.replace(/,/g, ""));

    if (
      numericValue < (loanInfo?.loanStarting as number) ||
      numericValue > (loanInfo?.loanLimit as number)
    ) {
      setLoanIsValid(false);
    } else {
      setLoanIsValid(true);
    }

    handleLoanAmountRequest();
  }

  function handleLoanAmountSliderChange(
    event: Event,
    value: number | number[],
    activeThumb: number
  ): void {
    setLoanIsValid(true);
    setFormData({ ...formData, requestAmount: value as number });
  }

  function handleLoanAmountRequest(): void {
    requestLoanSimulation();
  }

  function handleLoanDurationChange(
    event: React.SyntheticEvent<Element, Event> | null,
    newValue: string | null,
    label?: string | undefined
  ): void {
    setFormData({
      ...formData,
      durationId: newValue as string,
    });
  }

  function getLoanApplicationRequestBody(): LoanApplicationRecord {
    return {
      durationId: validateField<number>(
        Number(formData.durationId),
        "durationId"
      ),
      requestAmount: validateField<number>(
        formData.requestAmount,
        "requestAmount"
      ),
      simulationId: validateField<string>(
        formData.simulationId,
        "simulationId"
      ),
    };
  }

  function prepareToSetLoanContext(data: LoanApplicationDTO | undefined) {
    const isValid = data && simulationInfo && loanInfo;

    if (isValid) {
      setLoansContext({
        duration: `${validateField<number>(
          data.duration?.unit,
          "duration.unit"
        )} ${validateField<LoanDurationOptionDTO.chronoUnit>(
          data.duration?.chronoUnit,
          "duration.chronoUnit"
        )}`,
        interestRate: validateField<number>(data.interestRate, "interestRate"),
        loanRepay: validateField<number>(data.totalPayable, "installmentValue"),
        loanRequested: validateField<number>(
          data.requestAmount,
          "requestAmount"
        ),
        packageName: validateField<string>(loanInfo.loanPackage, "loanPackage"),
      });
    }
  }

  function handleLoanRequest() {
    if (formData && simulationInfo) {
      const requestBody = getLoanApplicationRequestBody();
      LoansApiService.postLoanApplication(requestBody).then((response) => {
        if (response && "data" in response) {
          prepareToSetLoanContext(response.data);
          navigate(LOANS_SUCCESS);
        }
      });
    }
  }

  function handleGoToDashboard() {
    navigate(DASHBOARD);
  }

  return (
    <div className="flex h-full w-full flex-col">
      {isEligible ? (
        <>
          <div className="mb-6 text-xl font-medium capitalize leading-7 text-zinc-900">
            How much would you like to apply for?
          </div>
          <div className="flex w-full flex-row gap-6">
            <div className="flex w-3/5 flex-col gap-12 rounded-lg border border-stone-300 p-8">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col items-start justify-start gap-8">
                  <div className="text-xl font-medium leading-7 text-zinc-900">
                    Request Loan Amount
                  </div>

                  <CurrencyFieldComponent
                    id="request-loan-amount-input"
                    value={formData.requestAmount}
                    className="h-12 w-64"
                    errorMessage={
                      "The amount you inserted is outside your approved limits"
                    }
                    hasError={!loanIsValid}
                    onBlur={(event) =>
                      handleLoanAmountInputBlur(event.target.value)
                    }
                    onChange={handleLoanAmountInputChange}
                    onKeyDown={(event) =>
                      event.code === "Enter" || event.code === "NumpadEnter"
                        ? handleLoanAmountInputBlur(
                            (event.target as HTMLInputElement).value
                          )
                        : event.stopPropagation()
                    }
                  />

                  <Slider
                    aria-label="Request Loan Amount"
                    defaultValue={formData.requestAmount || 0}
                    value={formData.requestAmount || 0}
                    color={
                      "error" as OverridableStringUnion<
                        "primary" | "secondary",
                        SliderPropsColorOverrides
                      >
                    }
                    step={LOAN_INCREMENTAL_VALUE}
                    min={loanInfo?.loanStarting}
                    max={loanInfo?.loanLimit}
                    onChange={handleLoanAmountSliderChange}
                    onChangeCommitted={handleLoanAmountRequest}
                  />

                  <div className="text-xl font-medium leading-7 text-zinc-900">
                    Select Loan Duration
                  </div>

                  <SelectComponent
                    items={options}
                    className="h-10 w-64 border-neutral-300"
                    iconColor="#1A1C1F"
                    defaultValue={formData.durationId}
                    label=""
                    labelId="analytics-select-label"
                    id="analytics-select"
                    value={formData.durationId}
                    onChange={handleLoanDurationChange}
                    showSelectedValueCheckIcon={true}
                    showDivider={true}
                  />
                </div>

                <div className="mt-8 flex w-full flex-col items-center justify-center rounded-lg bg-neutral-200">
                  <div className="flex w-full flex-row flex-nowrap items-center justify-between gap-6 p-8">
                    <div className="flex flex-col gap-1">
                      <div className="text-base font-normal leading-normal text-neutral-600">
                        Interest Rate
                      </div>
                      <div className="flex items-center gap-0.5 text-xl font-medium leading-7 text-neutral-900">
                        {simulationInfo?.interestRate || 0} %
                        <span className="text-xs capitalize leading-none text-neutral-600">
                          /{" "}
                          {simulationInfo?.chronoUnit
                            ?.toLowerCase()
                            .slice(0, 5)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="text-base font-normal leading-normal text-neutral-600">
                        Monthly Repayable
                      </div>
                      <div className="flex items-center gap-0.5 text-xl font-medium leading-7 text-neutral-900">
                        {formatCurrency(simulationInfo?.installmentValue) || 0}
                        <span className="text-xs capitalize leading-none text-neutral-600">
                          /{" "}
                          {simulationInfo?.chronoUnit
                            ?.toLowerCase()
                            .slice(0, 5)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="text-base font-normal leading-normal text-neutral-600">
                        Total Payable
                      </div>
                      <div className="text-xl font-medium leading-7 text-neutral-900">
                        {formatCurrency(simulationInfo?.totalPayable) || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {/* FIXME: CHANGE TO ACTUAL T&C URL */}
                <TermsAndConditionsComponent
                  text="on Apply"
                  to={TERMS_AND_CONDITIONS}
                />
              </div>
              <div className="flex flex-row items-center justify-center gap-1">
                <MajorButtonComponent
                  id="continue-button"
                  buttonType="primary"
                  className="h-12 w-large text-center text-base font-normal leading-normal text-white"
                  size="small"
                  type="button"
                  onClick={handleLoanRequest}
                  disabled={!loanIsValid || isLoading}
                >
                  Apply
                </MajorButtonComponent>
                {isLoading && (
                  <SpinnerComponent
                    type={"primary"}
                    showIcon={false}
                    size={48}
                    strokeWidth="40"
                  />
                )}
              </div>
            </div>

            <div className="flex h-72 w-2/5 flex-col items-start justify-start gap-10 rounded-lg border border-stone-300 p-8">
              <div className="flex flex-col gap-0.5">
                <span className="text-2xl font-medium capitalize leading-loose text-neutral-900">
                  {loanInfo?.loanPackage}
                </span>
                <span className="text-base font-normal leading-normal text-neutral-600">
                  {loanInfo?.businessSegment}
                </span>
              </div>

              <div className="flex h-28 flex-col items-start justify-start gap-4">
                <div className="flex gap-4 self-stretch">
                  <div className="flex shrink grow basis-0 flex-col items-start justify-start gap-1">
                    <span className="text-sm font-normal leading-none text-neutral-600">
                      Loan Starting
                    </span>
                    <span className="self-stretch text-xl font-medium leading-7 text-neutral-900">
                      {formatCurrency(loanInfo?.loanStarting)}
                    </span>
                  </div>
                  <div className="flex shrink grow basis-0 flex-col items-start justify-start gap-1">
                    <span className="text-sm font-normal leading-none text-neutral-600">
                      Loan Limit
                    </span>
                    <span className="text-xl font-medium leading-7 text-neutral-900">
                      {formatCurrency(loanInfo?.loanLimit)}
                    </span>
                  </div>
                </div>
                <hr className="w-full border-t-1 border-neutral-200" />
                <span className="self-stretch text-sm font-normal leading-none text-neutral-600">
                  Get access quick and affordable finance to fund your business
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="mt-40 flex shrink grow flex-col items-center justify-center gap-6">
          <div className="flex h-28 w-28 items-center justify-center rounded-full bg-orange-50 px-7 py-7">
            <div className="flex shrink grow basis-0 items-center justify-center self-stretch p-2">
              <Clock className="flex h-12 w-12" />
            </div>
          </div>

          <div className="text-center text-3.5xl font-medium leading-10 text-neutral-900">
            Loan Application Status
          </div>

          <div className="text-center text-base font-normal leading-normal text-neutral-600">
            <p>Loan eligibility review in progress.</p>
            <p>We'll update you with suitable options soon.</p>
            <br />
            <p>Thank you for your patience.</p>
          </div>

          <MajorButtonComponent
            id="loans-go-to-dashboard"
            buttonType="primary"
            size="large"
            onClick={handleGoToDashboard}
            type={"button"}
            className="mt-10"
          >
            Go to Dashboard
          </MajorButtonComponent>
        </div>
      )}
    </div>
  );
});

export default LoansPage;
